import { useState } from "react"

import {
  Intention,
  getAvailableStatusIntentions,
  getAvailableUserIntentions,
  isUserValidBOApprover,
} from "./checks"
import {
  approveBORevision,
  deleteBookingOrder,
  deleteBookingOrderLines,
  discardBORevision,
  markOfficialBookingOrder,
  postBookingOrder,
  postBookingOrderLines,
  postToIsapBookingOrder,
  rejectBORevision,
  revertPostBookingOrder,
  revertPostBookingOrderLines,
  saveBORevision,
  submitBORevision,
  updateBookingOrder,
} from "api/bookingOrder"
import { queryCache, useMutation } from "react-query"

import { ApiError } from "api/http/utils"
import { BookingOrder } from "models/bookingOrder"
import { validateProgramNames as _validateProgramNames } from "api/programs"
import { useAuthenticated } from "providers/A12n"

export function useBOLifecycle(
  id: string,
  bookingOrder?: Partial<BookingOrder>,
  isRevEditable?: boolean,
) {
  const { meta: userMeta } = useAuthenticated()

  const amIApprover = bookingOrder
    ? isUserValidBOApprover(bookingOrder, userMeta)
    : false

  const [errors, setErrors] = useState<Record<string, any>>()

  const invalidateBO = () => {
    setErrors(undefined)
    queryCache.invalidateQueries(["booking-order", id])
  }

  const handleError = (e: Error) => {
    const error = e as ApiError
    setErrors(error?.errorsMap)
  }

  const handleNestedError = (e: Error) => {
    const error = e as ApiError
    ;(error.fields || error.message) &&
      setErrors({ message: error.message, fields: error.fields })
  }

  const [update] = useMutation(updateBookingOrder, {
    onSuccess: invalidateBO,
    onError: handleError,
    throwOnError: true,
  })

  const [post, postStatus] = useMutation(postBookingOrder, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
    throwOnError: true,
  })

  const [unpost, unpostStatus] = useMutation(revertPostBookingOrder, {
    onSuccess: invalidateBO,
    onError: handleError,
    throwOnError: true,
  })

  const [postToIsap, { isLoading: postToIsapLoading }] = useMutation(
    postToIsapBookingOrder,
    {
      onSuccess: invalidateBO,
      onError: handleError,
      throwOnError: true,
    },
  )

  const [remove] = useMutation(deleteBookingOrder, {
    onError: handleError,
    throwOnError: true,
  })

  const [saveRe] = useMutation(saveBORevision, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
  })

  const [submitRe] = useMutation(submitBORevision, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
    throwOnError: true,
  })

  const [approveRe] = useMutation(approveBORevision, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
  })

  const [rejectRe] = useMutation(rejectBORevision, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
  })

  const [discardRe] = useMutation(discardBORevision, {
    onSuccess: invalidateBO,
    onError: invalidateBO,
  })

  const [removeLines] = useMutation(deleteBookingOrderLines, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
    throwOnError: true,
  })

  const [postLines, postLinesStatus] = useMutation(postBookingOrderLines, {
    onSuccess: invalidateBO,
    onError: handleNestedError,
    throwOnError: true,
  })

  const [unpostLines, unpostLinesStatus] = useMutation(
    revertPostBookingOrderLines,
    {
      onSuccess: invalidateBO,
      onError: handleNestedError,
      throwOnError: true,
    },
  )

  const [official, officialStatus] = useMutation(markOfficialBookingOrder, {
    onSuccess: invalidateBO,
    onError: handleError,
    throwOnError: true,
  })

  const [validateProgramNames] = useMutation(_validateProgramNames, {
    onSuccess: () => setErrors(undefined),
    onError: handleNestedError,
    throwOnError: true,
  })

  const actions = {
    update,
    POSTBO: [post, postStatus] as const,
    UNPOSTBO: [unpost, unpostStatus] as const,
    postToIsap,
    saveRe,
    approveRe,
    rejectRe,
    submitRe,
    discardRe,
    remove,
    removeLines,
    POSTLIS: [postLines, postLinesStatus] as const,
    UNPOSTLIS: [unpostLines, unpostLinesStatus] as const,
    validateProgramNames,
    MARKOFFICIAL: [official, officialStatus] as const,
  }

  const availableStatusIntentions = getAvailableStatusIntentions(
    bookingOrder?.status,
    bookingOrder?.viewType,
  )
  const availableIntentions = getAvailableUserIntentions(
    bookingOrder,
    userMeta,
    availableStatusIntentions,
  )
  const hasAnyAction = availableIntentions?.length > 0
  const hasAction = (action: Intention) => availableIntentions?.includes(action)

  const amIAllowedToEditRev = isRevEditable && !amIApprover
  const readonly = !amIAllowedToEditRev

  return {
    actions,
    postToIsapLoading,
    hasAction,
    hasAnyAction,
    readonly,
    errors,
  }
}
